import * as React from 'react'
import { ExpensesController } from '../../controllers'
import { saveAs } from 'file-saver'
import Notification from '../../utilities/Notification'
import PowerSelect from '../Form/PowerSelect'
import ModalHeader from './Parts/ModalHeader'
import ModalLoader from './Parts/ModalLoader'
import ModalMiddle from './Parts/ModalMiddle'
import ModalWindow from './Parts/ModalWindow'
import ModalContent from './Parts/ModalContent'
import TooltipError from '../Tooltips/ErrorTooltip'
import Button from '../Button/Button'
import { connect } from 'react-redux'
import { WithTranslation, withTranslation } from 'react-i18next'
import { AnyAction, Dispatch } from 'redux'
import { closeExportExpensesModal } from '../../store/modals/actions'
import ReactSelectTheme from '../Form/ReactSelectTheme'
import { BlobResponse, CurrentUser, ExpenseExportType, ExportTimePeriod } from '../../types'
import ExportHelper from '../../helpers/ExportHelper'
import moment from '../../utilities/Moment'
import ExpenseHelper from '../../helpers/ExpenseHelper'
import DateInput from '../Form/DateInput'
import { AppState } from '../../store'

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  close: typeof closeExportExpensesModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation

interface IState {
  didInitialLoad: boolean
  exportType: ExpenseExportType
  exportPeriodType: ExportTimePeriod
  start: string
  end: string
  errors: any
  isSubmitting: boolean
}

class ExportExpensesModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { start, end } = ExportHelper.getPeriodRange(ExportTimePeriod.ALL)

    this.state = {
      didInitialLoad: false,
      exportType: ExpenseExportType.RECEIPT,
      exportPeriodType: ExportTimePeriod.ALL,
      start: start,
      end: end,
      errors: {},
      isSubmitting: false,
    }

    this.onExportTypeSelect = this.onExportTypeSelect.bind(this)
    this.onExportPeriodTypeSelect = this.onExportPeriodTypeSelect.bind(this)
    this.onStartDateChange = this.onStartDateChange.bind(this)
    this.onEndDateChange = this.onEndDateChange.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onExpenseExportModalClose = this.onExpenseExportModalClose.bind(this)
    this.onErrorsDismiss = this.onErrorsDismiss.bind(this)
  }

  componentDidMount() {
    requestAnimationFrame(() => { this.setState({ didInitialLoad: true }) })
  }

  onExportTypeSelect(option) {
    this.setState({ exportType: option.value })
  }

  onExportPeriodTypeSelect(option) {
    const exportPeriodType = option.value

    const { start, end } = ExportHelper.getPeriodRange(exportPeriodType)

    this.setState({
      exportPeriodType: option.value,
      start: start,
      end: end
    })
  }

  onStartDateChange(value) {
    const newStartDate = moment(value)

    this.setState({
      start: newStartDate.isValid() ? newStartDate.format('YYYY-MM-DD') : null
    })
  }

  onEndDateChange(value) {
    const newEndDate = moment(value)

    this.setState({
      end: newEndDate.isValid() ? newEndDate.format('YYYY-MM-DD') : null
    })
  }


  async onFormSubmit(e) {
    e.preventDefault();

    const { close, t } = this.props
    const { exportType, start, end } = this.state

    this.setState({ isSubmitting: true })
    try {
      const response = await ExpensesController.exportExpenses({
        type: exportType,
        start: start,
        end: end
      })

      // @ts-ignore
      if (response.success) {
        Notification.notifySuccess(t('ExportExpensesModal::You\'ll receive the archive with expenses for the selected period via email.'))
      } else {
        // Blog destructering
        const { blob, filename } = response as BlobResponse

        // Use save as library
        saveAs(blob, filename)
      }

      close()
    } catch (ex) {
      console.error(ex)
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  onExpenseExportModalClose() {
    this.props.close()
  }

  onErrorsDismiss() {
    this.setState({
      errors: {}
    })
  }

  render() {
    const { t, currentUser: { workspace: { setting } } } = this.props
    const {
      didInitialLoad,
      errors,
      exportType,
      exportPeriodType,
      start,
      end,
      isSubmitting,
    } = this.state

    const exportTypeOptions = ExpenseHelper.getExpenseExportTypeOptions()
    const selectedExportTypeOption = exportTypeOptions.find(option => option.value === exportType)
    const exportPeriodOptions = ExportHelper.getPeriodOptions()
    const selectedExportPeriodType = exportPeriodOptions.find(option => option.value === exportPeriodType)

    return (
      <ModalWindow>
        <ModalHeader
          title={t('ExportExpensesModal::Export expenses')}
          onCloseClick={this.onExpenseExportModalClose}
        />

        {!didInitialLoad && <ModalLoader />}
        {didInitialLoad && <ModalMiddle>
          <ModalContent>
            <form onSubmit={this.onFormSubmit}>
              <div className='grid'>
                <div className='grid-cell with-6col'>
                  <div className='form-item'>
                    <label>
                      {t('ExportExpensesModal::Type')}
                    </label>

                    <PowerSelect
                      options={exportTypeOptions}
                      value={selectedExportTypeOption}
                      onChange={this.onExportTypeSelect}
                      noOptionsMessage={() => t('ExportExpensesModal::No option found')}
                      isClearable={false}
                      theme={ReactSelectTheme}
                    />
                  </div>
                </div>

                <div className='grid-cell with-6col'>
                  <div className='form-item'>
                    <label>
                      {t('ExportExpensesModal::Period')}
                    </label>

                    <PowerSelect
                      options={exportPeriodOptions}
                      value={selectedExportPeriodType}
                      onChange={this.onExportPeriodTypeSelect}
                      noOptionsMessage={() => t('ExportExpensesModal::No option found')}
                      isClearable={false}
                      theme={ReactSelectTheme}
                    />
                  </div>
                </div>

                {exportPeriodType === ExportTimePeriod.CUSTOM && <>
                  <div className='grid-cell with-6col'>
                    <div className='form-item'>
                      <label>
                        {t('ExportExpensesModal::Start date')}
                      </label>

                      <DateInput
                        name='start_date'
                        dateFormat={setting.date_format}
                        timeFormat={false}
                        value={moment(start)}
                        inputProps={{ placeholder: setting.date_format }}
                        onChange={this.onStartDateChange}
                        closeOnSelect
                      />
                    </div>
                  </div>

                  <div className='grid-cell with-6col'>
                    <div className='form-item'>
                      <label>
                        {t('ExportExpensesModal::End date')}
                      </label>
                      <DateInput
                        name='end_date'
                        dateFormat={setting.date_format}
                        timeFormat={false}
                        value={moment(end)}
                        inputProps={{ placeholder: setting.date_format }}
                        onChange={this.onEndDateChange}
                        closeOnSelect
                      />
                    </div>
                  </div>
                </>}
              </div>

              <input type='submit' style={{ display: 'none' }} />
            </form>
          </ModalContent>

          <div className='modal-footer'>
            <div />
            <div className='modal-footer-actions'>
              <div key='main-action' className='popover-wrapper'>
                <TooltipError
                  errors={errors}
                  onDismiss={this.onErrorsDismiss}
                />
                <Button
                  type='success'
                  text={t('ExportExpensesModal::Export')}
                  onClick={this.onFormSubmit}
                  isLoading={isSubmitting}
                />
              </div>
            </div>
          </div>
        </ModalMiddle>}
      </ModalWindow>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeExportExpensesModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ExportExpensesModal))